import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Container, Row, Col } from 'reactstrap';
import BreadcrumbWithFilter from 'src/components/Common/BreadcrumbWithFilter/BreadCrumbWithFilter';
import { ROUTER } from 'src/constants/routes';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducerState } from 'src/store/reducers';
import FeatherIcon from "feather-icons-react";
import {
  getWhatsappDocumentLink,
  getWhatsappMessageById,
  resetWhatsappMessageMessage,
  getEmployeeNumList,
  getDealerNumList,
  getCustomerNumList,
  getFranchiseNumList,
  getSupplierNumList,
  getManufacturerNumList
} from 'src/store/WhatsApp/action';
import { Link } from 'react-router-dom';
import FileImg from 'src/assets/images/files.png';
import PdfImg from 'src/assets/images/PDF-file.png';
import ExcelImg from 'src/assets/images/Excel-file.png';
import VideoImg from 'src/assets/images/video-file.png';
import { Image } from 'primereact/image';
import { API } from 'src/util/api';
import { useToast } from 'src/components/Common/ReactToaster';

const ViewWhatsappMessage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Select reducer states
  const {
    whatsAppDetails,
    error,
    message,
    loading,
    whatsAppDocumentLink,
    employeeNum,
    dealerNum,
    customerNum,
    franchiseNum,
    supplierNum,
    manufacturerNum
  } = useSelector((state: RootReducerState) => state.WhatsappReducer);

  // Layout states
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<'Sent' | 'Delivered' | 'Read' | 'Pending' | 'Failed'>('Sent');
  const [documentData, setDocumentData] = useState<any[]>([]);

  // Fetch data on mount
  useEffect(() => {
    dispatch(getWhatsappMessageById(id));
    dispatch(getEmployeeNumList());
    dispatch(getDealerNumList());
    dispatch(getCustomerNumList());
    dispatch(getFranchiseNumList());
    dispatch(getSupplierNumList());
    dispatch(getManufacturerNumList());
    return () => {
      dispatch(resetWhatsappMessageMessage());
    };
  }, [id, dispatch]);

  // Fetch attachments links
  useEffect(() => {
    if (whatsAppDetails?.documents?.length > 0) {
      whatsAppDetails.documents.forEach((element: any) => {
        dispatch(getWhatsappDocumentLink(element.id));
      });
    }
  }, [whatsAppDetails, dispatch]);

  // Format documents
  useEffect(() => {
    if (whatsAppDetails?.documents) {
      const data = whatsAppDetails.documents.map((item: any) => ({
        id: item.id,
        mimeType: item.mimeType,
        name: item.name,
        link: whatsAppDocumentLink?.length > 0 ? whatsAppDocumentLink.find((d: any) => d.id === item.id)?.link : ""
      }));
      setDocumentData(data);
    }
  }, [whatsAppDocumentLink, whatsAppDetails]);

  // Contact name lookup helper (backed by API loaded data)
  const getContactName = (phone: string) => {
    if (!phone) return "";
    const clean = (p: string) => p.replace(/\D/g, '');
    const target = clean(phone);
    if (!target) return "";

    const lists = [
      employeeNum,
      dealerNum,
      customerNum,
      franchiseNum,
      supplierNum,
      manufacturerNum
    ];

    for (const list of lists) {
      if (Array.isArray(list)) {
        const found = list.find((item: any) => clean(item.phone || '') === target);
        if (found && found.name) return found.name;
      }
    }
    return "";
  };

  // Avatar color generator based on name hash
  const getAvatarColor = (name: string) => {
    const colors = [
      '#e8f5e9', // light green
      '#fce4ec', // light pink
      '#e8eaf6', // light blue/purple
      '#efebe9', // light brown
      '#fff3e0', // light orange
      '#f3e5f5', // light lavender
      '#e0f7fa'  // light cyan
    ];
    const textColors = [
      '#2e7d32',
      '#c2185b',
      '#3f51b5',
      '#5d4037',
      '#f57c00',
      '#7b1fa2',
      '#0097a7'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return { bg: colors[index], text: textColors[index] };
  };

  // Document types
  const imageMimeTypes = ["image/jpg", "image/jpeg", "image/png", "image/gif", "image/bmp", "image/webp", "image/avif", "image/tiff", "image/svg+xml"];
  const pdfMimeTypes = ["application/pdf"];
  const excelMimeTypes = ["application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];
  const videoMimeTypes = ["video/mp4", "video/quicktime", "video/x-msvideo", "video/x-ms-wmv", "video/mpeg", "video/x-flv", "video/webm", "video/ogg", "video/3gpp", "video/3gpp2"];

  const previewImageDoc = documentData?.find((item: any) => imageMimeTypes.includes(item.mimeType) && item.link);
  const nonImageDocs = documentData?.filter((item: any) => !imageMimeTypes.includes(item.mimeType));

  // Count/Status Calculations - Strictly uses API data for Sent, Delivered, Read, Pending, and Failed states
  const auditLogs = whatsAppDetails?.bulkWhatsAppAuditLogDto || [];

  const allList = (whatsAppDetails?.allMobileNumber || []).filter((num: string) => num && num.trim() !== "");
  const sentList = (whatsAppDetails?.sentMobileNumber || []).filter((num: string) => num && num.trim() !== "");
  const failureList = (whatsAppDetails?.failureMobileNumber || []).filter((num: string) => num && num.trim() !== "");
  const pendingList = (whatsAppDetails?.pendingMobileNumber || []).filter((num: string) => num && num.trim() !== "");

  const total = whatsAppDetails?.totalCount || allList.length || auditLogs.length || 0;

  const hasLogs = auditLogs.length > 0;

  const sentCount = hasLogs
    ? auditLogs.filter((l: any) => {
      const s = l.status?.toLowerCase();
      return s === 'sent' || s === 'completed' || (l.isSent && !['read', 'delivered', 'failed', 'pending', 'penidng'].includes(s));
    }).length
    : (whatsAppDetails?.sentCount || sentList.length || 0);

  const deliveredCount = hasLogs
    ? auditLogs.filter((l: any) => l.status?.toLowerCase() === 'delivered').length
    : 0;

  const readCount = hasLogs
    ? auditLogs.filter((l: any) => l.status?.toLowerCase() === 'read').length
    : 0;

  const pendingCount = hasLogs
    ? auditLogs.filter((l: any) => ['pending', 'penidng'].includes(l.status?.toLowerCase())).length
    : (pendingList.length || 0);

  const failureCount = hasLogs
    ? auditLogs.filter((l: any) => l.status?.toLowerCase() === 'failed').length
    : (whatsAppDetails?.failureCount || failureList.length || 0);

  const getPercent = (count: number) => {
    if (!total) return "0.0";
    return ((count / total) * 100).toFixed(1);
  };

  // Contacts extractor - Backed strictly by API arrays
  const getContactsForTab = (tab: 'Sent' | 'Delivered' | 'Read' | 'Pending' | 'Failed') => {
    if (hasLogs) {
      const logsWithPhone = auditLogs.filter((l: any) => l.mobileNumber && l.mobileNumber.trim() !== "");
      switch (tab) {
        case 'Sent':
          return logsWithPhone.filter((l: any) => {
            const s = l.status?.toLowerCase();
            return s === 'sent' || s === 'completed' || (l.isSent && !['read', 'delivered', 'failed', 'pending', 'penidng'].includes(s));
          });
        case 'Delivered':
          return logsWithPhone.filter((l: any) => l.status?.toLowerCase() === 'delivered');
        case 'Read':
          return logsWithPhone.filter((l: any) => l.status?.toLowerCase() === 'read');
        case 'Pending':
          return logsWithPhone.filter((l: any) => ['pending', 'penidng'].includes(l.status?.toLowerCase()));
        case 'Failed':
          return logsWithPhone.filter((l: any) => l.status?.toLowerCase() === 'failed');
        default:
          return [];
      }
    } else {
      switch (tab) {
        case 'Sent':
          return sentList.map((num: string) => ({
            mobileNumber: num,
            isSent: true,
            status: 'Sent'
          }));
        case 'Delivered':
          return [];
        case 'Read':
          return [];
        case 'Pending':
          return pendingList.map((num: string) => ({
            mobileNumber: num,
            isSent: false,
            status: 'Pending'
          }));
        case 'Failed':
          return failureList.map((num: string) => ({
            mobileNumber: num,
            isSent: false,
            status: 'Failed',
            failureReason: 'Delivery failed'
          }));
        default:
          return [];
      }
    }
  };

  // Excel Export via API
  const handleExport = () => {
    if (!id) {
      showToast("Invalid WhatsApp message ID", { type: "error" });
      return;
    }

    API.exportExcelBulkWhatsAppApi(id)
      .then(() => {
        showToast("Excel exported successfully", { type: "success" });
      })
      .catch((error: any) => {
        showToast(error?.message || "Failed to export Excel", { type: "error" });
      });
  };


  // Click on stats row helper
  const handleStatusRowClick = (tab: 'Sent' | 'Delivered' | 'Read' | 'Pending' | 'Failed') => {
    setActiveTab(tab);
    setIsSidebarOpen(true);
  };

  // Rows metadata for WhatsApp canvas
  const statusRows = [
    {
      key: 'Sent',
      label: 'Sent to',
      count: sentCount,
      percent: getPercent(sentCount),
      icon: <FeatherIcon icon="check" size={15} color="#8696a0" />,
      tab: 'Sent' as const
    },
    {
      key: 'Delivered',
      label: 'Delivered to',
      count: deliveredCount,
      percent: getPercent(deliveredCount),
      icon: (
        <div style={{ display: 'inline-flex', alignItems: 'center' }}>
          <FeatherIcon icon="check" size={15} color="#8696a0" style={{ marginRight: '-7px' }} />
          <FeatherIcon icon="check" size={15} color="#8696a0" />
        </div>
      ),
      tab: 'Delivered' as const
    },
    {
      key: 'Read',
      label: 'Read by',
      count: readCount,
      percent: getPercent(readCount),
      icon: (
        <div style={{ display: 'inline-flex', alignItems: 'center' }}>
          <FeatherIcon icon="check" size={15} color="#34b7f1" style={{ marginRight: '-7px' }} />
          <FeatherIcon icon="check" size={15} color="#34b7f1" />
        </div>
      ),
      tab: 'Read' as const
    },
    {
      key: 'Pending',
      label: 'Pending',
      count: pendingCount,
      percent: getPercent(pendingCount),
      icon: <FeatherIcon icon="clock" size={15} color="#eab308" />,
      tab: 'Pending' as const
    },
    {
      key: 'Failed',
      label: 'Failed',
      count: failureCount,
      percent: getPercent(failureCount),
      icon: <FeatherIcon icon="alert-circle" size={15} color="#ef4444" />,
      tab: 'Failed' as const,
      isFailed: true
    }
  ];

  if (loading || !whatsAppDetails) {
    return (
      <div className="pagecontent">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "450px" }}>
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading WhatsApp details...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const currentTabContacts = getContactsForTab(activeTab);

  return (
    <React.Fragment>
      <style>{`
        .status-row-hover {
          transition: background-color 0.2s;
        }
        .status-row-hover:hover {
          background-color: #f1f8e9 !important;
        }
        .export-btn:hover {
          color: #054c44 !important;
          text-decoration: underline !important;
        }
        .close-btn:hover {
          color: #ef4444 !important;
        }
        .re-target-btn:hover {
          background-color: #054c44 !important;
        }
        .send-tmpl-btn:hover {
          background-color: #054c44 !important;
          transform: translateY(-1px);
        }
      `}</style>

      <div className="pagecontent" id="pagecontent">
        <Container fluid>
          <BreadcrumbWithFilter
            title="WhatsApp"
            titlePath={ROUTER.WHATSAPP_MESSAGE_LIST}
            breadcrumbItem="WhatsApp Details"
            isShowCreate={false}
            isShowFilter={false}
            filter={false}
            isShowFirstButton={true}
            firstButtonLabel="Export Excel"
            onFirstButtonClick={handleExport}
          />

          <Row className="mt-3">
            {/* Left/Middle: WhatsApp Chat Canvas */}
            <Col lg={isSidebarOpen ? 8 : 12} md={isSidebarOpen ? 7 : 12} className="mb-4">
              <div style={{
                borderRadius: '14px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid #d1d5db',
                backgroundColor: '#efeae2',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Cg fill='%23e4dbd2' fill-opacity='0.4'%3E%3Cpath d='M10 10c0 2.2-1.8 4-4 4S2 12.2 2 10s1.8-4 4-4 4 1.8 4 4zm20 30c0 2.2-1.8 4-4 4s-4-1.8-4-4 1.8-4 4-4 4 1.8 4 4zm30-20c0 2.2-1.8 4-4 4s-4-1.8-4-4 1.8-4 4-4 4 1.8 4 4zM16 64c0 3.3-2.7 6-6 6s-6-2.7-6-6 2.7-6 6-6 6 2.7 6 6zm44 4c0 2.2-1.8 4-4 4s-4-1.8-4-4 1.8-4 4-4 4 1.8 4 4zm-14-34c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8z'/%3E%3C/g%3E%3C/svg%3E")`,
                display: 'flex',
                flexDirection: 'column',
                height: '650px'
              }}>
                {/* Canvas Header */}
                <div style={{
                  backgroundColor: '#075e54',
                  padding: '12px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexShrink: 0
                }}>
                  <div style={{
                    width: '38px', height: '38px', borderRadius: '50%',
                    backgroundColor: '#25d366', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    <FeatherIcon icon="message-square" size={20} color="#fff" />
                  </div>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: '15px' }}>Campaign Broadcast</div>
                    <div style={{ color: '#b2dfdb', fontSize: '11px' }}>Sent to: {whatsAppDetails?.toUserTypes?.join(", ")}</div>
                  </div>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '14px' }}>
                    <FeatherIcon icon="video" size={18} color="#b2dfdb" />
                    <FeatherIcon icon="phone" size={18} color="#b2dfdb" />
                  </div>
                </div>

                {/* Canvas Body (Scrollable Stream) */}
                <div style={{
                  flexGrow: 1,
                  padding: '20px 16px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  {/* Bubble 1: Message Text & Media */}
                  <div style={{
                    backgroundColor: '#dcf8c6',
                    borderRadius: '12px 0 12px 12px',
                    padding: previewImageDoc ? '5px 5px 6px 5px' : '10px 12px 6px 12px',
                    maxWidth: '85%',
                    alignSelf: 'flex-end',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.12)'
                  }}>
                    {previewImageDoc && (
                      <div style={{ borderRadius: '8px', overflow: 'hidden', marginBottom: '6px' }}>
                        <Image
                          src={previewImageDoc.link}
                          alt="attachment"
                          style={{ width: "100%", display: "block" }}
                          imageStyle={{ width: "100%", height: "auto", maxHeight: "250px", objectFit: "cover" }}
                          preview
                        />
                      </div>
                    )}

                    {whatsAppDetails?.textContent && (
                      <div
                        style={{
                          fontSize: '13px',
                          color: '#111',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          padding: previewImageDoc ? '0 5px' : '0 4px'
                        }}
                        dangerouslySetInnerHTML={{ __html: whatsAppDetails.textContent.replaceAll('\\n', '<br />') }}
                      />
                    )}

                    {/* Non-image document attachments inside bubble */}
                    {nonImageDocs?.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px', padding: '0 4px' }}>
                        {nonImageDocs.map((item: any, index: number) => {
                          let imgSrc = FileImg;
                          if (pdfMimeTypes.includes(item.mimeType)) imgSrc = PdfImg;
                          else if (excelMimeTypes.includes(item.mimeType)) imgSrc = ExcelImg;
                          else if (videoMimeTypes.includes(item.mimeType)) imgSrc = VideoImg;
                          return (
                            <div key={index} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              backgroundColor: 'rgba(255,255,255,0.6)',
                              padding: '6px 8px',
                              borderRadius: '6px',
                              border: '1px solid rgba(0,0,0,0.05)'
                            }}>
                              <img src={imgSrc} alt="doc" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                              <Link to={item.link || ""} target="_blank" style={{ fontSize: '11px', color: '#075e54', fontWeight: '600', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>
                                {item.name || "Attachment"}
                              </Link>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: '3px',
                      marginTop: '4px',
                      paddingRight: previewImageDoc ? '4px' : '0'
                    }}>
                      <span style={{ fontSize: '10px', color: '#8696a0' }}>
                        {whatsAppDetails?.creationTime ? new Date(whatsAppDetails.creationTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ''}
                      </span>
                      <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <FeatherIcon icon="check" size={13} color="#34b7f1" style={{ marginRight: '-6px' }} />
                        <FeatherIcon icon="check" size={13} color="#34b7f1" />
                      </div>
                    </div>
                  </div>

                  {/* Bubble 2: Delivery Report Stats */}
                  <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '12px 0 12px 12px',
                    padding: '6px 0',
                    alignSelf: 'flex-end',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                    border: '1px solid #e2e8f0',
                    width: '100%',
                    maxWidth: '350px'
                  }}>
                    {statusRows.map((row) => (
                      <div
                        key={row.key}
                        className="status-row-hover"
                        onClick={() => handleStatusRowClick(row.tab)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 14px',
                          borderBottom: '1px solid #f8fafc',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '20px', display: 'flex', justifyContent: 'center' }}>
                            {row.icon}
                          </div>
                          <span style={{ fontSize: '12px', color: '#1e293b', fontWeight: 500 }}>
                            {row.count} - {row.label} {row.percent}%
                          </span>
                        </div>

                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {row.isFailed && (
                            <span style={{ fontSize: '10px', color: '#ef4444', fontWeight: 600 }}>
                              View reasons
                            </span>
                          )}
                          <FeatherIcon icon="chevron-right" size={14} color="#94a3b8" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Decorative Input Bar */}
                <div style={{
                  background: '#f0f0f0', padding: '8px 12px',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  flexShrink: 0
                }}>
                  <div style={{
                    flex: 1, background: '#fff', borderRadius: '20px',
                    padding: '7px 14px', fontSize: '13px', color: '#aaa',
                  }}>
                    Type a message
                  </div>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '50%',
                    background: '#25d366', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    <FeatherIcon icon="mic" size={16} color="#fff" />
                  </div>
                </div>
              </div>

            </Col>

            {/* Right: Broadcast Details Sidebar Panel */}
            {isSidebarOpen && (
              <Col lg={4} md={5} className="mb-4">
                <div style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '14px',
                  border: '1px solid #d1d5db',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '650px',
                  overflow: 'hidden'
                }}>
                  {/* Sidebar Header */}
                  <div style={{
                    padding: '16px 18px',
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>
                      Broadcast Details
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <FeatherIcon
                        icon="x"
                        size={18}
                        className="close-btn"
                        onClick={() => setIsSidebarOpen(false)}
                        style={{ color: '#64748b', cursor: 'pointer', transition: 'color 0.2s' }}
                      />
                    </div>
                  </div>

                  {/* Sidebar Tabs */}
                  <div style={{
                    display: 'flex',
                    borderBottom: '1px solid #f1f5f9',
                    backgroundColor: '#fafbfc',
                    flexShrink: 0
                  }}>
                    {(['Sent', 'Delivered', 'Read', 'Pending', 'Failed'] as const).map((tab) => {
                      const isActive = activeTab === tab;
                      return (
                        <div
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          style={{
                            flex: 1,
                            textAlign: 'center',
                            padding: '10px 2px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: isActive ? '#075e54' : '#64748b',
                            cursor: 'pointer',
                            borderBottom: isActive ? '3px solid #075e54' : '3px solid transparent',
                            backgroundColor: isActive ? '#ffffff' : 'transparent',
                            transition: 'all 0.2s'
                          }}
                        >
                          {tab}
                        </div>
                      );
                    })}
                  </div>

                  {/* Sidebar Contacts List */}
                  <div style={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    padding: '12px 18px'
                  }}>
                    {currentTabContacts.length > 0 ? (
                      currentTabContacts.map((contact: any, index: number) => {
                        const rawNum = contact.mobileNumber || '';
                        const resolvedName = getContactName(rawNum);
                        const displayName = resolvedName || rawNum;

                        // Only generate initials if name is resolved, otherwise use user icon
                        const initials = resolvedName ? resolvedName.replace(/\+/g, '').substring(0, 1).toUpperCase() : null;
                        const avatarColors = getAvatarColor(displayName);

                        return (
                          <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '8px 0',
                            borderBottom: '1px solid #f8fafc'
                          }}>
                            {/* Circular Avatar */}
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              backgroundColor: avatarColors.bg,
                              color: avatarColors.text,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: '700',
                              fontSize: '13px',
                              flexShrink: 0
                            }}>
                              {initials ? initials : <FeatherIcon icon="user" size={14} color={avatarColors.text} />}
                            </div>

                            {/* Info */}
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                              <span style={{
                                fontSize: '13px',
                                fontWeight: 700,
                                color: '#1e293b',
                                textTransform: 'uppercase',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {displayName}
                              </span>
                              {resolvedName && (
                                <span style={{ fontSize: '11px', color: '#64748b' }}>
                                  {rawNum}
                                </span>
                              )}
                              {activeTab === 'Failed' && contact.failureReason && (
                                <span style={{ fontSize: '10px', color: '#ef4444', fontStyle: 'italic', marginTop: '2px' }}>
                                  {contact.failureReason}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center text-muted mt-5" style={{ fontSize: '13px' }}>
                        No contacts found in this tab.
                      </div>
                    )}
                  </div>

                </div>
              </Col>
            )}
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ViewWhatsappMessage;